import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, CheckCircle2, Package, FileText, Award } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const Cashews = () => {
  const wholeKernels = [
    {
      id: 'w180',
      name: 'W180 Cashew Kernels',
      bestFor: 'Premium retail packs, gifting, high-end HoReCa',
      gradeMeaning: 'White whole kernels; large size class (premium appearance)',
      qualityFocus: 'Uniform colour, minimal defects, strong grading consistency',
      image: 'https://images.unsplash.com/photo-1756361946161-1cb809eae6cb?w=600'
    },
    {
      id: 'w210',
      name: 'W210 Cashew Kernels',
      bestFor: 'Premium retail, snack packs, HoReCa',
      gradeMeaning: 'White whole kernels; jumbo/large size class in trade grading',
      qualityFocus: 'Size uniformity + clean appearance',
      image: 'https://images.unsplash.com/photo-1687640511940-bcdee48f3ee0?w=600'
    },
    {
      id: 'w240',
      name: 'W240 Cashew Kernels',
      bestFor: 'Best balance of value + premium look; retail and HoReCa',
      gradeMeaning: 'White whole kernels; widely traded mid-large whole grade',
      qualityFocus: 'Consistent whole grading, great "everyday premium" SKU',
      image: 'https://images.unsplash.com/photo-1756361946161-1cb809eae6cb?w=600'
    },
    {
      id: 'w320',
      name: 'W320 Cashew Kernels',
      bestFor: 'Highest-volume whole grade for retail, food service, and ingredient buyers',
      gradeMeaning: 'White whole kernels; one of the most traded global whole grades',
      qualityFocus: 'Reliable supply, consistent grading, best value-performance',
      image: 'https://images.unsplash.com/photo-1687640511940-bcdee48f3ee0?w=600'
    },
    {
      id: 'w450',
      name: 'W450 Cashew Kernels',
      bestFor: 'Economical whole-kernel SKU for value retail, snack mixes, manufacturing',
      gradeMeaning: 'White whole kernels; smaller size class vs W320 (higher count per lb)',
      qualityFocus: 'Cost efficiency while retaining whole-kernel appearance',
      image: 'https://images.unsplash.com/photo-1756361946161-1cb809eae6cb?w=600'
    }
  ];

  const ingredientGrades = [
    {
      id: 'splits',
      name: 'Splits (S) — Cashew Splits',
      bestFor: 'Snack mixes, bakery toppings, confectionery, ingredient manufacturing',
      gradeMeaning: 'Broken-grade category; split halves from processing',
      qualityFocus: 'Uniform split size, low fines, clean packing'
    },
    {
      id: 'butts',
      name: 'Butts (B) — Cashew Butts',
      bestFor: 'Processing, granola/protein bars, bakery, confectionery',
      gradeMeaning: 'Broken-grade category; larger broken/halved forms',
      qualityFocus: 'Larger piece integrity, minimal powder fines'
    },
    {
      id: 'lwp',
      name: 'LWP — Large White Pieces',
      bestFor: 'Granola, bars, toppings, snack inclusions',
      gradeMeaning: 'Large uniform pieces, preferred for visible inclusions',
      qualityFocus: 'Piece uniformity and controlled fines'
    },
    {
      id: 'swp',
      name: 'SWP — Small White Pieces',
      bestFor: 'Spreads, sauces, coatings, bakery, confectionery',
      gradeMeaning: 'Smaller uniform pieces for smoother blending and consistent dosing',
      qualityFocus: 'Controlled particle range, minimal foreign matter'
    },
    {
      id: 'bb',
      name: 'BB — Baby Bits',
      bestFor: 'Industrial formulations, bakery mixes, coatings, fine inclusions',
      gradeMeaning: 'Tiny fragments ("bits") used where fine size distribution is required',
      qualityFocus: 'Controlled fines distribution + clean packing'
    }
  ];

  const grades = [
    {
      name: 'Standard Export',
      description: 'Best for bulk sourcing and value-led programs',
      features: ['Standard grading + batch COA', 'Aligned to buyer/destination specifications', 'Cost-effective bulk supply']
    },
    {
      name: 'Premium (EU/USA-Ready / Retail-Ready)',
      description: 'Best for private label, strict QC programs, and longer shelf-life retail packs',
      features: ['Tighter QC + enhanced traceability', 'Premium packaging options (vacuum/nitrogen flush)', 'Expanded COA panel as required']
    }
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/products/dry-fruits" className="inline-flex items-center text-[#263776] hover:text-[#F47D20] mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dry Fruits
            </Link>
            <div className="text-center">
              <h1 className="text-5xl font-bold mb-4">Cashew Kernels</h1>
              <p className="text-xl text-slate-700 mb-6">
                Premium whole kernels and graded pieces for retail, HoReCa, and ingredient manufacturing
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <span className="bg-[#263776] text-white px-4 py-2 rounded-full">10 Core SKUs</span>
                <span className="bg-[#F47D20] text-white px-4 py-2 rounded-full">UAE · UK-EU · USA · Africa</span>
                <span className="bg-slate-700 text-white px-4 py-2 rounded-full">Batch COA Always Supplied</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grading Options */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Grading Options</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {grades.map((grade, index) => (
                <Card key={index} className="p-6 border-t-4 border-t-[#263776]">
                  <h3 className="text-xl font-bold text-[#263776] mb-2">{grade.name}</h3>
                  <p className="text-slate-600 mb-4">{grade.description}</p>
                  <ul className="space-y-2">
                    {grade.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckCircle2 className="w-4 h-4 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Whole Kernels Section */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Whole Kernels (Retail + HoReCa)</h2>
              <p className="text-lg text-slate-600">Premium appearance grades for retail packs, gifting, and food service</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {wholeKernels.map((product) => (
                <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#263776] mb-2">{product.name}</h3>
                    <p className="text-sm text-[#F47D20] font-medium mb-3">{product.bestFor}</p>
                    
                    <Accordion type="single" collapsible className="mb-4">
                      <AccordionItem value="details" className="border-none">
                        <AccordionTrigger className="text-sm py-2">View Details</AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-2 text-sm">
                            <p><strong>Grade:</strong> {product.gradeMeaning}</p>
                            <p><strong>Quality Focus:</strong> {product.qualityFocus}</p>
                            <p><strong>Packaging:</strong> Bulk cartons | Vacuum/N₂ flush packs (on request)</p>
                            <p><strong>Documents:</strong> COA | Spec sheet | Lot-coded packing list | COO/Phyto as required</p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                    
                    <Button asChild size="sm" className="w-full bg-[#263776] hover:bg-[#1e2a5e]">
                      <Link to={`/contact?product=${product.id}`}>Request Quote</Link>
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ingredient Grades Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Ingredient Grades (Manufacturing)</h2>
              <p className="text-lg text-slate-600">Graded pieces for bakery, confectionery, snack mixes, and industrial applications</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {ingredientGrades.map((product) => (
                <Card key={product.id} className="p-6 hover:shadow-lg transition-shadow border-l-4 border-l-[#F47D20]">
                  <h3 className="text-lg font-bold text-[#263776] mb-2">{product.name}</h3>
                  <p className="text-sm text-[#F47D20] font-medium mb-3">{product.bestFor}</p>
                  
                  <div className="space-y-2 text-sm text-slate-600 mb-4">
                    <p><strong>Grade:</strong> {product.gradeMeaning}</p>
                    <p><strong>Quality Focus:</strong> {product.qualityFocus}</p>
                  </div>
                  
                  <div className="text-xs text-slate-500 mb-4">
                    <p><strong>Packaging:</strong> Bulk cartons</p>
                    <p><strong>Documents:</strong> COA | Spec sheet | Lot-coded packing list</p>
                  </div>
                  
                  <Button asChild size="sm" variant="outline" className="w-full border-[#263776] text-[#263776] hover:bg-[#263776] hover:text-white">
                    <Link to={`/contact?product=${product.id}`}>Request Quote</Link>
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SPANXPORT Baseline */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-[#263776] mb-6 text-center">SPANXPORT Quality Baseline</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#263776] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Package className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Moisture Control</h3>
                  <p className="text-sm text-slate-600">Typically controlled to 3%–5% (industry export benchmark)</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-[#F47D20] rounded-full flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Traceability</h3>
                  <p className="text-sm text-slate-600">Lot/production/date codes via packing list for representative sampling</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold mb-2">Inspection</h3>
                  <p className="text-sm text-slate-600">Pre-shipment inspection prior to loading (commonly specified)</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Packaging & Documents */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">Packaging & Documentation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="p-6">
                <h3 className="text-xl font-bold text-[#263776] mb-4">Packaging Options</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Bulk cartons</strong> — Standard B2B packaging</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Vacuum/Nitrogen-flush packs</strong> — Premium retail/private label</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Moisture/odour barrier</strong> — Program dependent</span>
                  </li>
                </ul>
              </Card>
              <Card className="p-6">
                <h3 className="text-xl font-bold text-[#263776] mb-4">Documents Supplied</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>COA</strong> — Certificate of Analysis</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Specification Sheet</strong></span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Packing List</strong> — Lot-coded</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>COO/Phytosanitary</strong> — As required</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20] mr-2 flex-shrink-0 mt-0.5" />
                    <span><strong>Buyer compliance docs</strong> — On request</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Global Footer Note */}
      <section className="py-8 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-300">
              All shipments supplied with <strong>batch COA</strong> and <strong>lot-coded traceability</strong>, aligned to buyer/destination requirements.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Request a Quote</h2>
          <p className="text-xl mb-4 max-w-2xl mx-auto">
            Share your requirements and we'll respond with offer + lead time + documentation list:
          </p>
          <div className="text-left max-w-md mx-auto mb-8 bg-white/10 p-6 rounded-lg">
            <ul className="space-y-2 text-sm">
              <li>• Destination country</li>
              <li>• Grade (W180, W210, W240, W320, W450, Splits, etc.)</li>
              <li>• Quantity</li>
              <li>• Pack type (bulk / vacuum / N₂ flush)</li>
              <li>• Target delivery window</li>
            </ul>
          </div>
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Request Detailed Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/products/dry-fruits">Back to Dry Fruits</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cashews;
